import { ColumnDef, TableOptions, getCoreRowModel } from '@tanstack/react-table'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import { FC, memo, useMemo } from 'react'
import { useTranslation } from '@crew/modules/i18n'
import BaselineError from '~icons/ic/baseline-error'
import { CrewPopover } from 'components/devextreme/crewPopover'
import { assertNever, uniqueString } from '@crew/utils'
import { BulkInvitationValidationResultCode } from '@crew/enums/domain'
import {
  VerifyBulkUpdateUsers_Row,
  VerifyBulkUpdateUsers_Row_Col,
} from '@crew/apis/user/models/verifyBulkUpdateUsers/response'

// Popoverの最大幅
const MAX_WIDTH_POPOVER = 300

// フォーマット不正の詳細コード、メッセージ
const InvalidFormat = {
  // 有効期限
  ExpirationDatetime: {
    key: 'expirationDatetime',
    message: 'message.tenant.invalidFormatExpirationDatetime',
  },
  // 有効/無効
  Enabled: {
    key: 'enabled',
    message: 'message.tenant.invalidFormatEnabled',
  },
  // 役割
  RoleCode: {
    key: 'roleCode',
    message: 'message.tenant.invalidFormatRoleCode',
  },
  // ログインID
  LoginId: {
    key: 'loginId',
    message: 'message.tenant.invalidFormatLoginId',
  },
} as const

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type InvalidFormat = (typeof InvalidFormat)[keyof typeof InvalidFormat]

type ItemStatusProps<T> = {
  item: VerifyBulkUpdateUsers_Row_Col<T>
  columnKey?: string
}

// 各項目のステータス表示コンポーネント
const ItemStatus = memo(<T,>(props: ItemStatusProps<T>) => {
  const { t } = useTranslation()
  // ユニークキー生成
  const popoverKey = useMemo(() => uniqueString(), [])

  // エラーメッセージ
  const errorMsg = useMemo(() => {
    // エラーが無ければ何も表示しない
    if (!props.item.hasError || !props.item.errorCode) {
      return null
    }

    // エラーコードに応じたメッセージを表示
    switch (props.item.errorCode) {
      case BulkInvitationValidationResultCode.RequiredItem:
        // 必須項目未入力
        return t(`message.tenant.requiredItem`)
      case BulkInvitationValidationResultCode.InvalidFormat:
        // 入力値フォーマット不正
        const invalidFormat = Object.values(InvalidFormat).find(
          (item) => item.key === props.columnKey
        )
        return t(invalidFormat?.message || `message.tenant.invalidFormat`)
      case BulkInvitationValidationResultCode.LoginIdDuplicate:
        // ログインID重複時
        return t(`message.signup.register.loginIdAlreadyExists`)
      case BulkInvitationValidationResultCode.PastDates:
        // 過去日を指定時
        return t(`message.general.pastDatesInvalid`)
      case BulkInvitationValidationResultCode.NotExistedUser:
        // 該当ユーザーなし
        return t(`message.tenant.notExistedUser`)
      case BulkInvitationValidationResultCode.NotExistedAccount:
        // 該当アカウントなし
        return t(`message.tenant.notExistedAccount`)
      default:
        assertNever(
          props.item.errorCode as never,
          'ItemStatus - unknown error code'
        )
    }
  }, [props.columnKey, props.item.errorCode, props.item.hasError, t])

  return (
    <div className="flex flex-row items-center gap-x-2.5">
      {/* エラーがある場合、エラー内容をポップオーバー表示 */}
      {props.item.hasError && (
        <div id={`error-${popoverKey}`}>
          <BaselineError width={20} height={20} className="crew-error-text" />
          <CrewPopover
            target={`#error-${popoverKey}`}
            showEvent="mouseenter"
            hideEvent="mouseleave"
            position="top"
            maxWidth={MAX_WIDTH_POPOVER}
          >
            <span className="text-sm">{errorMsg}</span>
          </CrewPopover>
        </div>
      )}
    </div>
  )
})

type BulkUpdateUserTableProps = {
  updateUsers: VerifyBulkUpdateUsers_Row[]
}

export const BulkUpdateUserTable: FC<BulkUpdateUserTableProps> = memo(
  (props) => {
    const { t } = useTranslation()

    // ユーザーIDが不正なレコードが存在するか
    const hasInvalidUserId = useMemo(
      () => props.updateUsers.some((user) => user.userId.hasError),
      [props.updateUsers]
    )

    const columns = useMemo<ColumnDef<VerifyBulkUpdateUsers_Row>[]>(() => {
      const cols: ColumnDef<VerifyBulkUpdateUsers_Row>[] = []

      // ユーザーIDが不正なレコードが存在する場合のみ列を追加
      // ⇒ユーザーID自体は表示しないが、エラー内容をポップオーバーで表示するため
      if (hasInvalidUserId) {
        cols.push({
          id: 'userId',
          cell: ({ row }) => (
            <div className="w-full flex items-center">
              <ItemStatus item={row.original.userId} />
            </div>
          ),
          size: 30,
        })
      }

      // 常時表示する列を追加
      cols.push(
        // 名前
        {
          id: 'name',
          header: () => t('label.displayName'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5">
              {row.original.displayName.originalValue}
              <ItemStatus item={row.original.displayName} />
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        // ログインID
        {
          id: 'loginId',
          header: () => t('label.loginId'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5">
              {row.original.loginId.originalValue}
              <ItemStatus item={row.original.loginId} columnKey="loginId" />
            </div>
          ),
          size: 160,
          minSize: 50,
        },
        // 役割
        {
          id: 'role',
          header: () => t('label.role'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5">
              {row.original.roleCode.originalValue}
              <ItemStatus item={row.original.roleCode} columnKey="roleCode" />
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        // 別名(ふりがな等)
        {
          id: 'alias',
          header: () => t('label.userAlias'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5">
              {row.original.alias?.originalValue}
              {row.original.alias && <ItemStatus item={row.original.alias} />}
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        // 検索キー
        {
          id: 'searchKey',
          header: () => t('label.searchKey'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5">
              {row.original.searchKey?.originalValue}
              {row.original.searchKey && (
                <ItemStatus item={row.original.searchKey} />
              )}
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        // 所属部門
        {
          id: 'department',
          header: () => t('label.department'),
          cell: ({ row }) => (
            <div className="w-full truncate flex items-center gap-x-2.5">
              {row.original.department?.originalValue}
              {row.original.department && (
                <ItemStatus item={row.original.department} />
              )}
            </div>
          ),
          size: 330,
          minSize: 50,
        },
        // 有効期限
        {
          id: 'expirationDate',
          header: () => t('label.expirationDate'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5">
              {row.original.expirationDatetime?.originalValue}
              {row.original.expirationDatetime && (
                <ItemStatus
                  item={row.original.expirationDatetime}
                  columnKey="expirationDatetime"
                />
              )}
            </div>
          ),
          size: 120,
          minSize: 50,
        },
        // 状態
        {
          id: 'enabled',
          header: () => t('label.memberState'),
          cell: ({ row }) => (
            <div className="w-full flex items-center gap-x-2.5">
              {row.original.enabled.originalValue}
              <ItemStatus item={row.original.enabled} columnKey="enabled" />
            </div>
          ),
          size: 120,
          minSize: 50,
        }
      )

      return cols
    }, [t, hasInvalidUserId])

    const tableOptions: TableOptions<VerifyBulkUpdateUsers_Row> = {
      data: props.updateUsers,
      columns,
      columnResizeMode: 'onChange',
      getCoreRowModel: getCoreRowModel(),
      meta: {
        headerRowHeight: 35,
        dataRowHeight: 40,
      },
    }

    return (
      <div className="max-h-96 flex">
        <div className="grow overflow-hidden">
          {/* new users table */}
          <CrewTable
            tableOptions={tableOptions}
            showPager={false}
            showColumnSelector={false}
          />
        </div>
      </div>
    )
  }
)
